import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ClickAwayListener, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { useFieldStyles } from 'components/UI/FieldStyles';
import LabelField from 'components/UI/LabelField';
import { FunctionComponent, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { Membership, MembershipEnrollmentType } from '@spike/membership-model';

export interface SelectMembershipProps {
    label?: string | JSX.Element;
    name?: string;
    selectedMembershipId?: number;
    placeholder?: string;
    memberships: Array<Membership>;
    disabled?: boolean;
    required?: boolean;
    id?: string;
    className?: string;
    onSelect: (membershipId: number) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            minWidth: 110
        },
        dropdownContainer: {
            position: 'relative',
            height: 54
        },
        collapsedContainer: {
            height: '100%'
        },
        expandedContainer: {
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #222222 !important',
            paddingBottom: 30,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 33
            }
        },
        headerContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 49,
            cursor: 'pointer'
        },
        header: {
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#000000',
            fontSize: 16,
            lineHeight: '20px'
        },
        searchInput: {
            'flex': 1,
            'minWidth': 0,
            'border': 'none',
            'outline': 'none',
            'background': 'transparent',
            'color': '#000000',
            'fontFamily': 'inherit',
            'fontSize': 16,
            'lineHeight': '27px',
            'paddingLeft': 17,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 19
            },
            '&::placeholder': {
                color: '#7A7A7A',
                opacity: 1
            }
        },
        listContainer: {
            borderTop: '1px solid #222222',
            overflowY: 'auto',
            height: 'auto',
            paddingTop: 13,
            maxHeight: 200,
            [theme.breakpoints.up('md')]: {
                paddingTop: 14
            }
        },
        placeholder: {
            color: '#7A7A7A',
            fontSize: 16,
            lineHeight: '27px'
        },
        border: {
            border: '1px solid #222222',
            borderRadius: 27,
            [theme.breakpoints.up('md')]: {
                borderRadius: 30
            }
        },
        textContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            paddingLeft: 17,
            paddingRight: 10,
            minWidth: 0,
            maxWidth: '85%',
            [theme.breakpoints.up('md')]: {
                paddingLeft: 19
            }
        },
        chevronContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            paddingRight: 32,
            [theme.breakpoints.up('md')]: {
                paddingRight: 35
            }
        },
        itemContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'center',
            'minHeight': 49,
            '&:hover': {
                background: '#F8F8F8'
            }
        },
        itemContent: {
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: 13,
            paddingRight: 14,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 14,
                paddingRight: 16
            }
        },
        itemDetails: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            minWidth: 0
        },
        name: {
            padding: '3px 5px 1px',
            fontSize: 16,
            lineHeight: '16px',
            [theme.breakpoints.up('md')]: {
                padding: '4px 6px 1px'
            },
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        price: {
            padding: '3px 5px',
            fontSize: 16,
            lineHeight: '16px',
            [theme.breakpoints.up('md')]: {
                padding: '4px 6px'
            }
        },
        typePill: {
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 16,
            marginLeft: 5,
            padding: '1px 7px',
            borderRadius: 10,
            backgroundColor: '#E8E8E8',
            color: '#4A4A4A',
            fontSize: 11,
            fontWeight: 500,
            lineHeight: '14px',
            [theme.breakpoints.up('md')]: {
                marginLeft: 6
            }
        },
        noResults: {
            padding: '6px 19px 4px',
            color: '#7A7A7A',
            fontSize: 16,
            lineHeight: '27px'
        },
        optionNotSelected: {
            border: '1px solid #D3D3D3 !important'
        },
        chevron: {
            fontSize: 16
        }
    })
);

export const SelectMembership: FunctionComponent<SelectMembershipProps> = props => {
    const classes = useStyles();
    const fieldClasses = useFieldStyles();

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [selectedId, setSelectedId] = useState(props.selectedMembershipId);
    const [query, setQuery] = useState('');

    useNonInitialEffect(() => {
        !isEqual(props.selectedMembershipId, selectedId) && setSelectedId(props.selectedMembershipId);
    }, [props.selectedMembershipId]);

    const collapse = () => {
        setIsCollapsed(true);
        setQuery('');
    };

    const clickCollapseHandler = () => {
        setIsCollapsed(prev => !prev);
        setQuery('');
    };

    const selectIdHandler = (id: number) => {
        setSelectedId(id);
        props.onSelect(id);
        collapse();
    };

    const normalizedQuery = query.trim().toLowerCase();
    const filteredMemberships = normalizedQuery
        ? props.memberships.filter(membership => (membership.name ?? '').toLowerCase().includes(normalizedQuery))
        : props.memberships;
    const selectedMembership = props.memberships.find(membership => membership.id === selectedId);
    const membershipTypeLabel = (membership: Membership) =>
        membership.enrollmentType === MembershipEnrollmentType.PACKAGE ? 'Package' : 'Membership';

    const header = (
        <Box
            className={classes.headerContainer}
            onClick={clickCollapseHandler}
        >
            <Box className={classes.textContainer}>
                <Typography className={clsx(classes.header, { [classes.placeholder]: selectedId === undefined })}>
                    {selectedMembership?.name ?? props.placeholder}
                </Typography>
                {selectedMembership && (
                    <Typography className={classes.typePill}>{membershipTypeLabel(selectedMembership)}</Typography>
                )}
            </Box>
            <Box className={classes.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={classes.chevron}
                />
            </Box>
        </Box>
    );

    const searchHeader = (
        <Box
            className={classes.headerContainer}
            style={{ cursor: 'default' }}
        >
            <input
                className={classes.searchInput}
                autoFocus
                placeholder={props.placeholder}
                value={query}
                onChange={event => setQuery(event.target.value)}
            />
            <Box
                className={classes.chevronContainer}
                style={{ cursor: 'pointer' }}
                onClick={collapse}
            >
                <FontAwesomeIcon
                    icon={faChevronUp}
                    color="#222222"
                    className={classes.chevron}
                />
            </Box>
        </Box>
    );

    const list = (
        <Box className={classes.listContainer}>
            {filteredMemberships.length === 0 ? (
                <Typography className={classes.noResults}>No memberships found</Typography>
            ) : (
                filteredMemberships.map((membership, index) => (
                    <Box
                        className={classes.itemContainer}
                        key={membership.id}
                        id={`selection_pet_${index}`}
                    >
                        <Box
                            className={classes.itemContent}
                            onClick={() => selectIdHandler(membership.id ?? 0)}
                        >
                            <Box className={classes.itemDetails}>
                                <Typography className={classes.name}>{membership.name}</Typography>
                                <Typography className={classes.typePill}>{membershipTypeLabel(membership)}</Typography>
                            </Box>
                            <Typography className={classes.price}>
                                {membership.total ? `$${Number(membership.total).toFixed(2)}` : ''}
                            </Typography>
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );

    const collapsed = (
        <Box className={clsx(classes.border, classes.collapsedContainer, [classes.optionNotSelected])}>{header}</Box>
    );

    const expanded = (
        <Box className={clsx(classes.border, classes.expandedContainer)}>
            {searchHeader}
            {list}
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={collapse}>
            <Box className={clsx(classes.container, fieldClasses.container, props.className)}>
                <LabelField {...props} />
                <Box
                    className={classes.dropdownContainer}
                    id="select_membership"
                >
                    {isCollapsed ? collapsed : expanded}
                </Box>
            </Box>
        </ClickAwayListener>
    );
};

export default SelectMembership;
